import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { CataloguePageHeader } from '@/components/catalogue/Header';
import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import AddressBookSelector from '@/components/form/fields/address/AddressBookSelector';
import AddressField from '@/components/form/fields/address/AddressField';
import ContactInformationField from '@/components/form/fields/address/ContactInformationField';
import { TextField } from '@/components/form/fields/text/TextInput';
import SiChevronLeft from '@/icons/SiChevronLeft';
import { useCurrentPurchaseOrderLink } from '@/lib/getMyLinks';
import { cn } from '@/lib/utils';
import { Contact } from '@/services/api/commonTypes';
import { PurchaseOrderDeliveryOption } from '@/services/api/contracts/catalog/bundles';
import { useAddress } from '@/services/api/hooks/address';
import { useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { useContactDetail } from '@/services/api/hooks/contact';
import { useCatalogueRoutePrefix } from '@/services/catalogue/useCatalogueRoutePrefix';
import { PODeliveryAddress } from '@/services/purchaseOrders/purchaseOrder.types';
import {
  usePoContact,
  usePoDelivery,
  usePoName,
  useStorePurchaseOrderDeliveryAddress,
  useStorePurchaseOrderDeliveryContact
} from '@/services/purchaseOrders/usePurchaseOrderService';
import { PurchaseOrderStep, usePONavigateAndValidate } from '@/services/purchaseOrders/validation/purchaseOrderHooks';
import { mapNewAddressToLegacyFormat } from '@/utils/address';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';
import SavePurchaseOrderButton from './SavePurchaseOrder';

interface DeliveryOptionCardProps {
  title: string;
  description: string;
  onClickCallback: () => void;
  selected?: boolean;
}

const DeliveryOptionCard: FC<DeliveryOptionCardProps> = ({ title, description, onClickCallback, selected }) => {
  return (
    <div className="flex items-center space-x-4">
      <div
        onClick={onClickCallback}
        className={cn(
          'w-6 cursor-pointer h-6 rounded-full border flex items-center justify-center',
          selected ? 'bg-primary-700' : ''
        )}
      >
        {selected && <div className="w-2 h-2 rounded-full border bg-white"></div>}
      </div>
      <div onClick={onClickCallback} className="cursor-pointer w-full flex flex-col leading-tight">
        <p className="text-xl font-semibold">{title}</p>
        <p>{description}</p>
      </div>
    </div>
  );
};

const DeliveryDetailFields = z.object({
  address: PODeliveryAddress,
  contact: Contact
});

export type DeliveryDetailFields = z.infer<typeof DeliveryDetailFields>;

export const PurchaseOrderDeliveryDetailsPage: FC = () => {
  const navigateAndValidate = usePONavigateAndValidate();

  const poLink = useCurrentPurchaseOrderLink();
  const poName = usePoName();
  const nameToDisplay = poName || 'Create';
  const { partnerId, companyId, orderId } = useParams();
  if (!(partnerId && companyId && orderId)) throw new Error('Missing params');

  const breadcrumbs: NavPage[] = [
    {
      name: 'Orders / Quotes',
      to: poLink
    },
    {
      name: nameToDisplay,
      to: `${poLink}/${orderId}/poCatalogue`
    },
    {
      name: 'Delivery Details',
      to: ''
    }
  ];

  const routePrefix = useCatalogueRoutePrefix();

  const defaultPODelivery = usePoDelivery();

  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<PurchaseOrderDeliveryOption | undefined>(
    defaultPODelivery?.type
  );

  const [defaultAddress] = useAddress(partnerId, companyId, defaultPODelivery?.addressId);

  const poContact = usePoContact();
  const [defaultContact] = useContactDetail(poContact?.contactId);

  const methods = useForm<DeliveryDetailFields>({
    resolver: zodResolver(DeliveryDetailFields),
    defaultValues: {
      address:
        defaultAddress === null
          ? undefined
          : { name: defaultAddress.name, ...mapNewAddressToLegacyFormat(defaultAddress) },
      contact: defaultContact === null ? undefined : defaultContact
    }
  });

  const storeAddress = useStorePurchaseOrderDeliveryAddress();

  const debouncedSaveAddress = useDebouncedCallback(
    async (deliveryOption: PurchaseOrderDeliveryOption, address?: PODeliveryAddress, updateBackend?: boolean) => {
      await storeAddress(deliveryOption, address, updateBackend);
    },
    400
  );

  const storeContact = useStorePurchaseOrderDeliveryContact();

  const debouncedSaveContact = useDebouncedCallback(
    async (deliveryOption: PurchaseOrderDeliveryOption, contact: Contact, updateBackend?: boolean) => {
      await storeContact(deliveryOption, contact, updateBackend);
    },
    400
  );

  const handleAddressBookChange = useCallback((payload: any) => {
    const opts = { shouldValidate: true, shouldDirty: true };
    methods.setValue('address.addressLine1', payload.line1 || '', opts);
    methods.setValue('address.addressLine2', payload.line2 || '', opts);
    methods.setValue('address.townCity', payload.city || '', opts);
    methods.setValue('address.stateCounty', payload.state || '', opts);
    methods.setValue('address.postalCode', payload.postcode || '', opts);
    methods.setValue('address.country', payload.country || '', opts);
  }, [methods]);

  const address = methods.watch('address');
  useEffect(() => {
    const updateAddress = async () => {
      if (selectedDeliveryOption !== undefined) {
        const validAddress = await methods.trigger('address');

        if (
          selectedDeliveryOption !== PurchaseOrderDeliveryOption.Custom ||
          (selectedDeliveryOption === PurchaseOrderDeliveryOption.Custom && validAddress)
        ) {
          await debouncedSaveAddress(selectedDeliveryOption, address, methods.formState.isDirty);
        }
      }
    };
    updateAddress().catch(console.error);
  }, [
    address?.name,
    address?.country,
    address?.townCity,
    address?.postalCode,
    address?.stateCounty,
    address?.addressLine1,
    address?.addressLine2,
    storeAddress,
    selectedDeliveryOption
  ]);

  const contact = methods.watch('contact');
  useEffect(() => {
    const updateContact = async () => {
      if (selectedDeliveryOption !== undefined) {
        const validContact = await methods.trigger('contact');

        if (
          selectedDeliveryOption !== PurchaseOrderDeliveryOption.Custom ||
          (selectedDeliveryOption === PurchaseOrderDeliveryOption.Custom && validContact)
        ) {
          await debouncedSaveContact(selectedDeliveryOption, contact, methods.formState.isDirty);
        }
      }
    };
    updateContact().catch(console.error);
  }, [contact?.workEmail, contact?.personalEmail, contact?.telephoneNumber, selectedDeliveryOption]);

  const onProceed = async () => {
    const validAddress = await methods.trigger('address');
    const validContact = await methods.trigger('contact');

    const canProceed =
      (selectedDeliveryOption === PurchaseOrderDeliveryOption.Custom && validAddress && validContact) ||
      selectedDeliveryOption !== PurchaseOrderDeliveryOption.Custom;

    if (canProceed) {
      navigateAndValidate(`${routePrefix}/poCatalogue/confirm`);
    }
  };

  const [mspDetails] = useMspDetailsForCustomer(partnerId, companyId);

  return (
    <>
      <div className="flex h-full flex-1 flex-col bg-white bg-opacity-50">
        <CataloguePageHeader title={nameToDisplay} subtitle={'Delivery Details'} breadcrumbs={breadcrumbs}>
          <div className="flex gap-4">
            {/*<HelpButton />*/}
            <Button
              onClick={() => {
                navigateAndValidate(`${routePrefix}/poCatalogue`, PurchaseOrderStep.SELECT_PRODUCTS);
              }}
              variant={ButtonVariant.Mono}
            >
              <SiChevronLeft className="text-xl" />
              <span>Back to Catalogue</span>
            </Button>
            <SavePurchaseOrderButton />
            <Button variant={ButtonVariant.Primary} disabled={selectedDeliveryOption === undefined} onClick={onProceed}>
              Continue Order
            </Button>
          </div>
        </CataloguePageHeader>
        <div className="px-6 py-8 space-y-4 overflow-y-auto">
          <p className="text-lg font-bold">Where would you like the products to be delivered?</p>

          <DeliveryOptionCard
            title="Daas Stock Order"
            // TODO plug in msp name once available to the customer
            description="Use this option if you want the stock sent to DaaS, and to arrange delivery through DaaS."
            selected={selectedDeliveryOption === PurchaseOrderDeliveryOption.Daas}
            onClickCallback={() => {
              setSelectedDeliveryOption(PurchaseOrderDeliveryOption.Daas);
            }}
          />
          <DeliveryOptionCard
            title="Partner Stock Order"
            description={`Use this option if you want the stock sent to ${mspDetails.name}, and to arrange delivery through them.`}
            selected={selectedDeliveryOption === PurchaseOrderDeliveryOption.Msp}
            onClickCallback={() => {
              setSelectedDeliveryOption(PurchaseOrderDeliveryOption.Msp);
            }}
          />
          <DeliveryOptionCard
            title="Custom Address"
            description="Use this option if you want the stock sent directly to you"
            selected={selectedDeliveryOption === PurchaseOrderDeliveryOption.Custom}
            onClickCallback={() => {
              if (selectedDeliveryOption !== PurchaseOrderDeliveryOption.Custom) {
                methods.resetField('address');
              }
              setSelectedDeliveryOption(PurchaseOrderDeliveryOption.Custom);
            }}
          />

          {selectedDeliveryOption === PurchaseOrderDeliveryOption.Custom && (
            <div className="space-y-1">
              <p className="text-xl font-bold">Custom Address</p>
              <Form className="flex w-full flex-col space-y-4" methods={methods} onSubmit={() => {}}>
                <AddressBookSelector 
                  mspId={partnerId} 
                  customerId={companyId} 
                  onChange={handleAddressBookChange}
                />
                <AddressField />

                <div className="space-y-2">
                  <p className="text-xl font-bold">Recipient Contact Information</p>
                  <ContactInformationField nameFieldId="address.name" />
                </div>
              </Form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
